<template>
    <section class="uses-tailwind px-5">
        <div class="flex items-center justify-between mb-5">
            <div class="flex items-center gap-2"></div>
            <div class="flex items-center gap-2">
                <ui5-button
                    class="border border-primary hover:text-primary"
                    v-on:click="toggleCreateModal(true)"
                >
                    {{ trans('material_types.actions.create_material_type') }}
                </ui5-button>
            </div>
        </div>
        <Table
            :columns="tableColumns"
            :data="materialTypes"
            :actions="tableActions"
            :loading="isLoadingMaterialTypes"
        />
        <modal
            :show="createModal.show"
            :loading="createModal.loading"
            :actions="createModal.actions"
            @close="toggleCreateModal(false)"
            :title="trans('material_types.create_modal.title')"
        >
            <ui5-label class="text-lg">
                {{ trans('material_types.form.labels.name') }}
            </ui5-label>
            <ui5-input
                class="w-full"
                v-bind:value="createModal.form.name"
                v-on:change="
                    $set(createModal.form, 'name', $event.target.value)
                "
            />
        </modal>
    </section>
</template>

<script>
import localization from '../mixins/localization';
import Loader from '../components/loader/Loader';
import Table from '../components/table/Table';
import Modal from '../components/modals/Modal';
import {
    showConfirmDialog,
    showErrorMessage,
    showInfoMessage,
} from '../dialogs';
import materialTypesApi from '../api/materialTypes';

export default {
    data() {
        return {
            materialTypes: [],
            isLoadingMaterialTypes: false,
            createModal: {
                show: false,
                loading: false,
                form: {
                    name: '',
                },
                actions: [
                    {
                        label: this.trans(
                            'material_types.create_modal.actions.cancel'
                        ),
                        handler: () => {
                            this.toggleCreateModal(false);
                        },
                    },
                    {
                        label: this.trans(
                            'material_types.create_modal.actions.create'
                        ),
                        theme: 'primary',
                        handler: () => {
                            this.createMaterialType();
                        },
                    },
                ],
            },
        };
    },
    computed: {
        tableColumns() {
            return [
                {
                    key: 'name',
                    label: this.trans('material_types.table.columns.name'),
                },
            ];
        },
        tableActions() {
            return [
                {
                    icon: 'fa fa-eye',
                    title: this.trans('material_types.tooltips.view'),
                    action: (row) => {
                        this.goToMaterialType(row.id);
                    },
                },
                {
                    icon: 'fa fa-copy',
                    title: this.trans('material_types.tooltips.duplicate'),
                    action: (row) => {
                        this.duplicateMaterialType(row.id);
                    },
                },
            ];
        },
    },
    mounted() {
        this.fetchMaterialTypes();
    },
    methods: {
        fetchMaterialTypes() {
            this.isLoadingMaterialTypes = true;

            materialTypesApi
                .fetchAll()
                .then((response) => {
                    if (!response.data || !response.data.data) {
                        throw new Error();
                    }

                    this.materialTypes = response.data.data;
                })
                .catch((error) => {
                    showErrorMessage(
                        this.trans(
                            'material_types.errors.fetching_material_types'
                        )
                    );
                })
                .finally(() => {
                    this.isLoadingMaterialTypes = false;
                });
        },
        toggleCreateModal(open = true) {
            this.$set(this.createModal, 'show', open);
        },
        toggleCreateModalLoading(loading = true) {
            this.$set(this.createModal, 'loading', loading);
        },
        createMaterialType() {
            this.toggleCreateModalLoading(true);

            materialTypesApi
                .create(this.createModal.form)
                .then((response) => {
                    if (!response.data || !response.data.data) {
                        throw new Error();
                    }

                    const materialType = response.data.data;

                    this.toggleCreateModal(false);
                    this.goToMaterialType(materialType.id);
                })
                .catch((error) => {
                    showErrorMessage(
                        this.trans(
                            'material_types.errors.creating_material_type'
                        )
                    );

                    this.toggleCreateModal(false);
                })
                .finally(() => {
                    this.toggleCreateModalLoading(false);
                });
        },
        goToMaterialType(id) {
            this.$router.push(`/material-types/${id}`);
        },
        duplicateMaterialType(id) {
            this.isLoadingMaterialTypes = true;

            materialTypesApi
                .duplicate(id)
                .then((response) => {
                    if (!response.data || !response.data.data) {
                        throw new Error();
                    }

                    showInfoMessage(
                        this.trans(
                            'material_types.feedback.material_type_duplicated'
                        )
                    );

                    this.fetchMaterialTypes();
                })
                .catch((error) => {
                    showErrorMessage(
                        this.trans(
                            'material_types.errors.duplicating_material_type'
                        )
                    );

                    this.isLoadingMaterialTypes = false;
                });
        },
    },
    mixins: [localization],
    components: {
        Loader,
        Modal,
        Table,
    },
};
</script>
